// Scrolling animations: reveal public-page content as the visitor scrolls.
// Each .ss-component starts hidden (transparent + offset via CSS) and fades /
// slides up once it enters the viewport. Gated site-wide by the body class
// `ss-scroll-animate`, which design_header.php emits server-side -- so it is
// present at first paint and there is no flash of visible-then-hidden content.
// Pairs with webroot/sass/general/_scroll-animations.scss.
//
// design_header.php also arms a fail-safe timer (window.ssScrollAnimateFallbackTimer)
// that strips the body class -- revealing everything -- if this module never runs.
// If that timer fires before <body> exists, it retries on DOMContentLoaded.
// We cancel the timer and the retry on execution so a successful load is not undone.
//
// Observation starts only once layout has settled (window load, with a safety
// fallback). Components contain images/sliders that load asynchronously; if we
// observed at DOMContentLoaded the still-collapsed sections would all intersect
// the viewport at once and reveal together, so nothing would animate on scroll.

const BODY_CLASS: string = 'ss-scroll-animate';
const COMPONENT_SELECTOR: string = '.ss-component';
const IN_VIEW_CLASS: string = 'ss-in-view';
const BOUND_FLAG: string = 'ssScrollAnimateBound';
// Reveal once the top of a component has scrolled ~12% of the viewport into view.
const ROOT_MARGIN: string = '0px 0px -12% 0px';
const THRESHOLD: number = 0;
// Safety net so content is never left hidden if `load` is slow or never fires.
const LAYOUT_SETTLE_FALLBACK_MS: number = 2500;

type ScrollAnimateWindow = Window & {
    ssScrollAnimateFallbackTimer?: number;
    ssScrollAnimateFallbackCancelled?: boolean;
    ssScrollAnimateFallback?: () => void;
};

let initialized: boolean = false;

const prefersReducedMotion = (): boolean =>
    Boolean(window.matchMedia) && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const reveal = (element: HTMLElement): void => {
    element.classList.add(IN_VIEW_CLASS);
};

const revealAll = (): void => {
    document.querySelectorAll<HTMLElement>(COMPONENT_SELECTOR).forEach(reveal);
};

const observeComponent = (observer: IntersectionObserver, element: HTMLElement): void => {
    if (element.dataset[BOUND_FLAG]) return;
    element.dataset[BOUND_FLAG] = '1';
    observer.observe(element);
};

const observeAddedComponents = (observer: IntersectionObserver): void => {
    if (!window.MutationObserver) return;

    const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) return;
                if (node.matches(COMPONENT_SELECTOR)) observeComponent(observer, node);
                node.querySelectorAll<HTMLElement>(COMPONENT_SELECTOR).forEach((child) => {
                    observeComponent(observer, child);
                });
            });
        });
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
};

const startObserving = (): void => {
    if (initialized) return;
    initialized = true;

    if (!document.body.classList.contains(BODY_CLASS)) return;

    // No IntersectionObserver (or reduced motion): show everything immediately.
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        revealAll();
        return;
    }

    const observer: IntersectionObserver = new IntersectionObserver(
        (entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                reveal(entry.target as HTMLElement);
                obs.unobserve(entry.target);
            });
        },
        { rootMargin: ROOT_MARGIN, threshold: THRESHOLD }
    );

    document.querySelectorAll<HTMLElement>(COMPONENT_SELECTOR).forEach((element) => {
        observeComponent(observer, element);
    });
    observeAddedComponents(observer);
};

// The body class (added server-side) hides components from first paint. Cancel
// design_header.php's fail-safe now that we are running, so it does not strip
// the class (including a DOMContentLoaded retry if the timer already fired).
const cancelFallbackTimer = (): void => {
    const scrollAnimateWindow = window as ScrollAnimateWindow;
    scrollAnimateWindow.ssScrollAnimateFallbackCancelled = true;
    if (scrollAnimateWindow.ssScrollAnimateFallbackTimer !== undefined) {
        window.clearTimeout(scrollAnimateWindow.ssScrollAnimateFallbackTimer);
    }
    if (scrollAnimateWindow.ssScrollAnimateFallback) {
        document.removeEventListener('DOMContentLoaded', scrollAnimateWindow.ssScrollAnimateFallback);
    }
};

cancelFallbackTimer();

// Wait until layout settles before observing so above-the-fold content animates
// on load while everything below the fold waits for the scroll into view.
if (document.readyState === 'complete') {
    startObserving();
} else {
    const onLayoutSettled = (): void => {
        window.clearTimeout(layoutTimer);
        window.removeEventListener('load', onLayoutSettled);
        startObserving();
    };
    window.addEventListener('load', onLayoutSettled, { once: true });
    const layoutTimer: number = window.setTimeout(onLayoutSettled, LAYOUT_SETTLE_FALLBACK_MS);
}
